import { z } from 'zod';

// ===== ENUMS & DISCRIMINATORS =====
export const STOCK_TYPE = z.enum(['chemical', 'fertilizer']);
export type StockType = z.infer<typeof STOCK_TYPE>;

export const ORDER_STATUS = z.enum(['Pending', 'Received', 'Partially Received', 'Cancelled']);
export type OrderStatus = z.infer<typeof ORDER_STATUS>;

export const TRANSACTION_TYPE = z.enum([
  'PURCHASE_RECEIPT',
  'TASK_APPLICATION',
  'STOCKTAKE',
  'SPRAY_RESERVED',
  'ADJUSTMENT',
  'TRANSFER',
]);
export type TransactionType = z.infer<typeof TRANSACTION_TYPE>;

export const AUDIT_STATUS = z.enum(['Draft', 'Completed']);
export type AuditStatus = z.infer<typeof AUDIT_STATUS>;

// ===== STOCK ITEM (Chemical or Fertilizer) =====
export const StockItemSchema = z.object({
  id: z.number().int().positive(),
  stock_type: STOCK_TYPE,
  company_id: z.string().uuid(),
  name: z.string(), // product (chemical) or fertilzer (fertilizer)
  on_hand_quantity: z.number().nonnegative(),
  minimum_threshold: z.number().nonnegative(),
  pack_size_litres_kg: z.string().nullable().optional(),
  cost_per_unit: z.number().nonnegative(),
  status: z.enum(['in_stock', 'low_stock', 'out_of_stock']).default('in_stock'),
  created_at: z.date(),
  updated_at: z.date(),
});
export type StockItem = z.infer<typeof StockItemSchema>;

// ===== STOCK ORDER =====
export const StockOrderSchema = z.object({
  id: z.number().int().positive(),
  stock_type: STOCK_TYPE,
  company_id: z.string().uuid(),
  distributor_id: z.number().int().positive().nullable().optional(),
  shed_id: z.number().int().positive().nullable().optional(),
  po_number: z.string().max(50),
  invoice_number: z.string().max(255).nullable().optional(),
  order_date: z.date().nullable().optional(),
  status: ORDER_STATUS.default('Pending'),
  special_instructions: z.string().nullable().optional(),
  total_amount: z.number().nonnegative(),
  gst_amount: z.number().nonnegative(),
  invoice_path: z.string().nullable().optional(),
  created_at: z.date(),
  updated_at: z.date(),
});
export type StockOrder = z.infer<typeof StockOrderSchema>;

// ===== STOCK ORDER ITEM =====
export const StockOrderItemSchema = z.object({
  id: z.number().int().positive(),
  order_id: z.number().int().positive(),
  stock_item_id: z.number().int().positive(),
  qty_ordered: z.number().positive(),
  unit_cost: z.number().nonnegative(),
  pack_size: z.string().nullable().optional(),
  batch_number: z.string().max(100).nullable().optional(),
  invoice_number: z.string().max(100).nullable().optional(),
  created_at: z.date(),
  updated_at: z.date(),
});
export type StockOrderItem = z.infer<typeof StockOrderItemSchema>;

// ===== STOCK MOVEMENT =====
export const StockMovementSchema = z.object({
  id: z.number().int().positive(),
  stock_item_id: z.number().int().positive(),
  stock_type: STOCK_TYPE,
  company_id: z.string().uuid(),
  shed_id: z.number().int().positive().nullable().optional(),
  transaction_type: TRANSACTION_TYPE,
  reference_id: z.number().int().nullable().optional(),
  reference_type: z.string().max(50).nullable().optional(),
  quantity_change: z.number(),
  pack_size: z.string().nullable().optional(),
  batch_number: z.string().max(100).nullable().optional(),
  invoice_number: z.string().max(100).nullable().optional(),
  expiry_date: z.date().nullable().optional(),
  notes: z.string().nullable().optional(),
  created_at: z.date(),
  updated_at: z.date(),
});
export type StockMovement = z.infer<typeof StockMovementSchema>;

// ===== STOCK AUDIT =====
export const StockAuditSchema = z.object({
  id: z.number().int().positive(),
  company_id: z.string().uuid(),
  audit_date: z.date(),
  status: AUDIT_STATUS.default('Draft'),
  notes: z.string().nullable().optional(),
  created_at: z.date(),
  updated_at: z.date(),
});
export type StockAudit = z.infer<typeof StockAuditSchema>;

export const StockAuditItemSchema = z.object({
  id: z.number().int().positive(),
  audit_id: z.number().int().positive(),
  stock_item_id: z.number().int().positive(),
  stock_type: STOCK_TYPE,
  shed_id: z.number().int().positive().nullable().optional(),
  system_stock: z.number(),
  actual_stock: z.number(),
  discrepancy: z.number(),
  notes: z.string().nullable().optional(),
  created_at: z.date(),
  updated_at: z.date(),
});
export type StockAuditItem = z.infer<typeof StockAuditItemSchema>;

// ===== DASHBOARD STATS =====
export const DashboardStatsSchema = z.object({
  total_items: z.number().int().nonnegative(),
  in_stock_count: z.number().int().nonnegative(),
  low_stock_count: z.number().int().nonnegative(),
  out_of_stock_count: z.number().int().nonnegative(),
  outstanding_orders: z.number().int().nonnegative(),
  total_order_value: z.number().nonnegative(),
  total_inventory_value: z.number().nonnegative(),
  total_invoice_count: z.number().int().nonnegative(),
  total_invoice_value: z.number().nonnegative(),
  items_by_category: z.record(z.number()).optional(),
});
export type DashboardStats = z.infer<typeof DashboardStatsSchema>;

// ===== REQUEST/RESPONSE SCHEMAS =====
export const CreateOrderRequestSchema = z.object({
  stock_type: STOCK_TYPE,
  distributor_id: z.number().int().positive().nullable().optional(),
  shed_id: z.number().int().positive().nullable().optional(),
  po_number: z.string().max(50).min(1),
  order_date: z.date().optional(),
  special_instructions: z.string().nullable().optional(),
  items: z.array(
    z.object({
      stock_item_id: z.number().int().positive(),
      qty_ordered: z.number().positive(),
      unit_cost: z.number().nonnegative(),
      pack_size: z.string().nullable().optional(),
      batch_number: z.string().max(100).nullable().optional(),
    })
  ).min(1),
});
export type CreateOrderRequest = z.infer<typeof CreateOrderRequestSchema>;

// YYYY-MM-DD — the calendar date written to the `order_date` DATE column.
const ISO_DATE = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Date must be YYYY-MM-DD');

export const UpdateOrderRequestSchema = z.object({
  po_number: z.string().max(50).optional(),
  invoice_number: z.string().max(255).nullable().optional(),
  // Invoice date (manual invoices) / order date (purchase orders). Distinct from created_at.
  order_date: ISO_DATE.optional(),
  status: ORDER_STATUS.optional(),
  items: z.array(
    z.object({
      id: z.number().int().positive(),
      qty_ordered: z.number().positive().optional(),
      unit_cost: z.number().nonnegative().optional(),
      batch_number: z.string().max(100).nullable().optional(),
    })
  ).optional(),
});
export type UpdateOrderRequest = z.infer<typeof UpdateOrderRequestSchema>;

export const GetOrdersQuerySchema = z.object({
  stock_type: STOCK_TYPE.optional(),
  status: ORDER_STATUS.optional(),
  invoice_number: z.string().trim().max(100).optional(),
  page: z.coerce.number().int().positive().default(1),
  per_page: z.coerce.number().int().positive().max(100).default(25),
});
export type GetOrdersQuery = z.infer<typeof GetOrdersQuerySchema>;

// ===== CART / ORDER STOCK WORKFLOW =====

export const CartItemSchema = z.object({
  id: z.number().int().positive(),                     // chemical_frontend.id or fertilzer_frontend.id
  type: STOCK_TYPE,
  order_type: z.enum(['manual', 'purchase']),
  name: z.string(),
  qty: z.number().int().positive(),
  price: z.number().nonnegative(),                     // price per pack (price_per_lkg × pack_size_num)
  pack_size: z.string(),                               // e.g. "20.5"
  product_code: z.string().nullable().optional(),
  batch_number: z.string().nullable().optional(),
  invoice_number: z.string().nullable().optional(),
  shed_id: z.number().int().positive(),
  shed_name: z.string(),
  supplier_id: z.number().int().positive(),
  supplier_name: z.string(),
  supplier_address: z.string().nullable().optional(),
  has_product_data: z.boolean(),
});
export type CartItem = z.infer<typeof CartItemSchema>;

export const SaveCartRequestSchema = z.object({
  cart: z.array(CartItemSchema).min(1),
  special_instructions: z.string().nullable().optional(),
  // The date printed on the supplier's invoice (manual orders). Stored in order_date;
  // the entry date stays in created_at. Omitted → today in the company's timezone.
  invoice_date: ISO_DATE.optional(),
});
export type SaveCartRequest = z.infer<typeof SaveCartRequestSchema>;

export const SupplierInfoSchema = z.object({
  distributor_id: z.number(),
  company: z.string(),
  street: z.string().nullable().optional(),
  postcode: z.string().nullable().optional(),
  available_units: z.string().nullable().optional(),
  has_product_data: z.boolean(),
});
export type SupplierInfo = z.infer<typeof SupplierInfoSchema>;

export const PackSizeOptionSchema = z.object({
  unit: z.string(),
  product_code: z.string().nullable().optional(),
});
export type PackSizeOption = z.infer<typeof PackSizeOptionSchema>;

// ===== STOCK TAKE / AUDIT SCHEMAS =====

export const SaveStockTakeRequestSchema = z.object({
  type: STOCK_TYPE,
  item_id: z.number().int().positive(),
  shed_id: z.number().int().positive().nullable().optional(),
  new_level_litres: z.number().nullable().optional(),
  confirm_only: z.boolean().optional(),
  confirm_stock: z.boolean().optional(),
  batch_number: z.string().nullable().optional(),
  notes: z.string().nullable().optional(),
  audit_id: z.number().int().positive().nullable().optional(),
});
export type SaveStockTakeRequest = z.infer<typeof SaveStockTakeRequestSchema>;

export const AuditListItemSchema = z.object({
  id: z.number().int().positive(),
  audit_date: z.string(),
  status: AUDIT_STATUS,
  items_count: z.number().int().nonnegative(),
  pending_count: z.number().int().nonnegative(),
  chem_pending: z.number().int().nonnegative(),
  fert_pending: z.number().int().nonnegative(),
  stock_type: z.string(),
  notes: z.string().nullable().optional(),
});
export type AuditListItem = z.infer<typeof AuditListItemSchema>;

export const AuditDetailItemSchema = z.object({
  id: z.number().int().positive(),
  item_id: z.number().int().positive(),
  item_type: STOCK_TYPE,
  product_name: z.string(),
  shed_name: z.string().nullable().optional(),
  batch_number: z.string().nullable().optional(),
  system_stock: z.number(),
  actual_stock: z.number(),
  discrepancy: z.number(),
  notes: z.string().nullable().optional(),
  audit_date: z.string().nullable().optional(),
});
export type AuditDetailItem = z.infer<typeof AuditDetailItemSchema>;

export const ShedInventoryItemSchema = z.object({
  id: z.number().int().positive(),
  product_name: z.string(),
  batch_number: z.string(),
  current_stock: z.number(),
  current_stock_litres: z.number(),
  product_total_litres: z.number(),
  pack_size: z.string(),
  unit: z.string(),
  reference_number: z.string().nullable().optional(),
});
export type ShedInventoryItem = z.infer<typeof ShedInventoryItemSchema>;
